import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { LiveToggle } from "@/components/LiveToggle";
import { useLiveSource } from "@/hooks/useLiveSource";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, ArrowUpRight, Wallet, CreditCard, Banknote,
  Eye, Zap, AlertCircle, CheckCircle2, Clock, XCircle, ShieldAlert, ShieldCheck, Play,
} from "lucide-react";

const STATUS_BUCKETS = {
  success: { icon: CheckCircle2, color: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  pending: { icon: Clock, color: "text-amber-700 border-amber-300 bg-amber-50" },
  failed: { icon: XCircle, color: "text-red-700 border-red-300 bg-red-50" },
};

const KIND_LABEL = {
  bbps: "BBPS",
  payout: "Payout",
  partner_topup: "Partner Top-up",
  customer_topup: "Customer Top-up",
};

const KIND_ICON = {
  bbps: Banknote, payout: ArrowUpRight,
  partner_topup: Wallet, customer_topup: CreditCard,
};

const PAYMODE_TABS = [
  { key: "all", label: "All Transactions", icon: Wallet },
  { key: "approved_pending", label: "Approved - Pending Credit", icon: Play },
  { key: "non_whitelisted", label: "Non-Whitelisted Paymode", icon: ShieldAlert },
];

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
};

export default function AdminOutboundTxns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Data source toggle (global)
  const { source, setSource, relayConfigured, isPreview } = useLiveSource();

  // Section tabs
  const [paymodeTab, setPaymodeTab] = useState("all");

  // Filters
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [gateway, setGateway] = useState("all");
  const [search, setSearch] = useState("");
  const [sinceHours, setSinceHours] = useState(168);

  // Detail
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Reconcile dialog
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileTxnId, setReconcileTxnId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      if (source === "live") {
        // Fetch from live DB via relay — merge wallet_topups + customer_wallet_topups
        const tables = ["wallet_topups", "customer_wallet_topups"];
        const allRows = [];
        for (const table of tables) {
          const { data } = await api.post("/admin/system-test/live-db", {
            table, filters: {}, sort_field: "created_at", sort_dir: -1, limit: 250,
          });
          if (data.rows) {
            data.rows.forEach(r => allRows.push({
              ...r,
              id: r.id,
              kind: table === "wallet_topups" ? "partner_topup" : "customer_topup",
              amount: parseFloat(r.amount || 0),
              status: r.status,
              status_bucket: ["success", "credited"].includes(r.status) ? "success" : ["failed", "paymode_rejected"].includes(r.status) ? "failed" : "pending",
              gateway: r.gateway,
              provider_ref: r.provider_ref || r.provider_payment_id || "",
              txnid: r.provider_order_id || "",
              target_wallet: table === "wallet_topups" ? "partner" : "customer",
              target_label: table === "wallet_topups" ? (r.user_id || "") : (r.mobile || ""),
              created_by_label: r.partner_id || r.user_id || "",
              created_at: r.created_at,
              processed_at: r.credited_at,
              wallet_credited: r.wallet_credited || false,
              credit_status: r.credit_status || "",
              pending_paymode_id: r.pending_paymode_id || "",
              payment_details: r.payment_details || {},
              table,
            }));
          }
        }
        allRows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setRows(allRows);
      } else {
        const params = { kind, status, since_hours: sinceHours, limit: 500, paymode_filter: paymodeTab };
        if (gateway !== "all") params.gateway = gateway;
        if (search.trim()) params.search = search.trim();
        const { data } = await api.get("/outbound-txns", { params });
        setRows(data.items || []);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load outbound transactions");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status, gateway, sinceHours, paymodeTab, source]);

  const stats = useMemo(() => {
    const byBucket = { success: 0, pending: 0, failed: 0 };
    const amtByBucket = { success: 0, pending: 0, failed: 0 };
    rows.forEach((r) => {
      const b = r.status_bucket || "pending";
      byBucket[b] = (byBucket[b] || 0) + 1;
      amtByBucket[b] = (amtByBucket[b] || 0) + (r.amount || 0);
    });
    return { byBucket, amtByBucket, total: rows.length };
  }, [rows]);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/outbound-txns/${row.id}`);
      setDetail({ ...row, raw: data.row, table: data.table });
      setDetailOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load details");
    }
  };

  const reconcileSingle = async () => {
    if (!reconcileTxnId.trim()) { toast.error("Enter a txnid or internal id"); return; }
    setReconciling(true);
    try {
      const { data } = await api.post(`/outbound-txns/reconcile/${reconcileTxnId.trim()}`);
      toast.success(`Reconcile: ${data.outcome.bucket} — ${data.outcome.detail}`);
      setReconcileOpen(false);
      setReconcileTxnId("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reconcile failed");
    } finally { setReconciling(false); }
  };

  const reconcileAll = async () => {
    if (!window.confirm("Sweep ALL pending/initiated topups from last 72h and pull live status from gateway?")) return;
    setReconciling(true);
    try {
      const { data } = await api.post("/outbound-txns/reconcile/all?since_hours=72");
      toast.success(`Scanned ${data.scanned} · Credited ${data.credited || 0} · Failed ${data.failed || 0} · Still pending ${data.still_pending || 0}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk reconcile failed");
    } finally { setReconciling(false); }
  };

  const processApproved = async () => {
    if (!window.confirm("Process ALL topups whose paymode has been approved? This will credit the wallets.")) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/outbound-txns/process-approved");
      if (data.released_count > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Process failed");
    } finally { setProcessing(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="overline">Money Movement Ledger</span>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="outbound-title">
              Outbound Transactions
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Every BBPS bill payment, payout, partner wallet top-up, and customer wallet top-up.
              Use the tabs below to view paymode-specific subsections.
            </p>
          </div>
          <div className="flex gap-2">
            <LiveToggle source={source} setSource={setSource} relayConfigured={relayConfigured} isPreview={isPreview} />
            <Button onClick={() => setReconcileOpen(true)} variant="outline" data-testid="reconcile-targeted-btn">
              <Zap className="h-4 w-4 mr-1.5" /> Reconcile by txnid
            </Button>
            <Button onClick={reconcileAll} disabled={reconciling} data-testid="reconcile-all-btn">
              {reconciling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Reconcile all pending
            </Button>
          </div>
        </div>

        {/* Paymode Section Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="paymode-tabs">
          {PAYMODE_TABS.map(({ key, label, icon: TabIcon }) => (
            <Button
              key={key}
              size="sm"
              variant={paymodeTab === key ? "default" : "outline"}
              onClick={() => setPaymodeTab(key)}
              className="gap-1.5"
              data-testid={`paymode-tab-${key}`}
            >
              <TabIcon className="h-3.5 w-3.5" /> {label}
            </Button>
          ))}
        </div>

        {/* Approved Pending banner with Process button */}
        {paymodeTab === "approved_pending" && rows.length > 0 && (
          <div className="surface-card border-l-4 border-l-amber-500 p-4 flex items-center justify-between" data-testid="approved-pending-banner">
            <div>
              <div className="font-semibold text-amber-800">
                {rows.length} transaction{rows.length > 1 ? "s" : ""} with approved paymode pending credit
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Total: {formatINR(rows.reduce((s, r) => s + (r.amount || 0), 0))} — These paymodes are approved. Click "Process All" to credit the wallets.
              </div>
            </div>
            <Button onClick={processApproved} disabled={processing} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="process-approved-btn">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Process All
            </Button>
          </div>
        )}

        {/* Non-whitelisted banner */}
        {paymodeTab === "non_whitelisted" && rows.length > 0 && (
          <div className="surface-card border-l-4 border-l-red-500 p-4" data-testid="non-whitelisted-banner">
            <div className="font-semibold text-red-800">
              {rows.length} transaction{rows.length > 1 ? "s" : ""} with non-whitelisted payment mode
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              These topups are held because the payment instrument is not approved. Go to Payment Mode Whitelist to approve/reject.
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" count={stats.total} amount={Object.values(stats.amtByBucket).reduce((a,b)=>a+b,0)} icon={Wallet} accent="text-muted-foreground" />
          <StatCard label="Success" count={stats.byBucket.success} amount={stats.amtByBucket.success} icon={CheckCircle2} accent="text-emerald-700" />
          <StatCard label="Pending" count={stats.byBucket.pending} amount={stats.amtByBucket.pending} icon={Clock} accent="text-amber-700" />
          <StatCard label="Failed" count={stats.byBucket.failed} amount={stats.amtByBucket.failed} icon={XCircle} accent="text-red-700" />
        </div>

        {/* Filters */}
        <div className="surface-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Search</label>
            <div className="flex gap-1 mt-1">
              <Input value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()}
                placeholder="txnid, id, mobile, provider ref…" data-testid="outbound-search" />
              <Button variant="outline" onClick={load}><Search className="h-4 w-4" /></Button>
            </div>
          </div>
          <FilterSelect label="Kind" value={kind} setValue={setKind} testId="filter-kind"
            options={[["all","All kinds"], ["bbps","BBPS"], ["payout","Payouts"], ["partner_topup","Partner top-ups"], ["customer_topup","Customer top-ups"]]} />
          <FilterSelect label="Status" value={status} setValue={setStatus} testId="filter-status"
            options={[["all","All"], ["success","Success"], ["pending","Pending"], ["failed","Failed"]]} />
          <FilterSelect label="Gateway" value={gateway} setValue={setGateway} testId="filter-gateway"
            options={[["all","All"], ["razorpay","Razorpay"], ["cashfree","Cashfree"], ["manarecharge","ManaRecharge"], ["runpaisa","Runpaisa"]]} />
          <FilterSelect label="Window" value={String(sinceHours)} setValue={(v)=>setSinceHours(Number(v))} testId="filter-window"
            options={[["24","Last 24h"], ["72","Last 3 days"], ["168","Last 7 days"], ["720","Last 30 days"]]} />
        </div>

        {/* Table */}
        <div className="surface-card overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {paymodeTab === "approved_pending" ? "No approved-paymode transactions pending credit." :
               paymodeTab === "non_whitelisted" ? "No non-whitelisted paymode transactions." :
               "No outbound transactions in this window."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Kind</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  {paymodeTab !== "all" && <th className="px-3 py-2 text-left">Credit Status</th>}
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-left">Created by</th>
                  <th className="px-3 py-2 text-left">PG / Gateway</th>
                  <th className="px-3 py-2 text-left">Provider ref</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const StatusIcon = STATUS_BUCKETS[r.status_bucket]?.icon || Clock;
                  const KindIcon = KIND_ICON[r.kind] || Wallet;
                  const cs = r.credit_status || "";
                  return (
                    <tr key={`${r.table}-${r.id}`} className="hover:bg-accent/30 cursor-pointer" onClick={() => openDetail(r)} data-testid={`outbound-row-${r.id}`}>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtIST(r.created_at)}</td>
                      <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5 text-xs"><KindIcon className="h-3.5 w-3.5" />{KIND_LABEL[r.kind] || r.kind}</span></td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(r.amount)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${STATUS_BUCKETS[r.status_bucket]?.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />{r.status}
                        </Badge>
                      </td>
                      {paymodeTab !== "all" && (
                        <td className="px-3 py-2">
                          {cs === "pending_paymode_approval" && (
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                              <ShieldAlert className="h-3 w-3 mr-1" />Pending Approval
                            </Badge>
                          )}
                          {cs === "paymode_rejected" && (
                            <Badge variant="outline" className="text-[10px] text-red-700 border-red-300 bg-red-50">
                              <XCircle className="h-3 w-3 mr-1" />Rejected
                            </Badge>
                          )}
                          {cs === "credited" && (
                            <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50">
                              <CheckCircle2 className="h-3 w-3 mr-1" />Credited
                            </Badge>
                          )}
                          {!cs && <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs"><span className="text-muted-foreground">{r.target_wallet}:</span> {r.target_label}</td>
                      <td className="px-3 py-2 text-xs">{r.created_by_label}</td>
                      <td className="px-3 py-2 text-xs">{r.gateway || r.provider || "—"}{r.pg ? ` · ${r.pg}` : ""}</td>
                      <td className="px-3 py-2 text-xs font-mono text-muted-foreground truncate max-w-[140px]">{r.provider_ref || r.txnid || "—"}</td>
                      <td className="px-3 py-2"><Eye className="h-4 w-4 text-muted-foreground" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5" />
              {detail ? `${KIND_LABEL[detail.kind] || detail.kind} · ${formatINR(detail.amount)}` : "Transaction"}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Detail k="Status" v={<Badge variant="outline" className={STATUS_BUCKETS[detail.status_bucket]?.color}>{detail.status}</Badge>} />
                <Detail k="Credit Status" v={detail.credit_status || "—"} />
                <Detail k="Internal ID" v={<span className="font-mono text-xs">{detail.id}</span>} />
                <Detail k="Provider txnid" v={<span className="font-mono text-xs">{detail.txnid || "—"}</span>} />
                <Detail k="Provider ref / UTR" v={<span className="font-mono text-xs">{detail.provider_ref || "—"}</span>} />
                <Detail k="Created at" v={fmtIST(detail.created_at)} />
                <Detail k="Processed at" v={fmtIST(detail.processed_at)} />
                <Detail k="Created by" v={detail.created_by_label} />
                <Detail k="Target wallet" v={`${detail.target_wallet} · ${detail.target_label}`} />
                <Detail k="Gateway" v={detail.gateway || "—"} />
                <Detail k="PG channel" v={detail.pg || "—"} />
                <Detail k="Purpose" v={detail.purpose || "—"} />
                <Detail k="Wallet credited" v={detail.wallet_credited ? "Yes" : detail.kind && ["partner_topup","customer_topup"].includes(detail.kind) ? "Not yet" : "n/a"} />
              </div>
              {detail.payment_details && Object.keys(detail.payment_details).length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Payment Details</div>
                  <div className="grid grid-cols-2 gap-2 bg-muted rounded-md p-3">
                    {Object.entries(detail.payment_details).map(([k,v]) => v ? (
                      <div key={k}><span className="text-[10px] uppercase text-muted-foreground">{k}</span><div className="text-xs font-mono">{String(v)}</div></div>
                    ) : null)}
                  </div>
                </div>
              )}
              {detail.status_bucket === "pending" && (
                <Button onClick={async ()=>{
                  setReconciling(true);
                  try {
                    const { data } = await api.post(`/outbound-txns/reconcile/${detail.id}`);
                    toast.success(`${data.outcome.bucket} — ${data.outcome.detail}`);
                    setDetailOpen(false); load();
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  finally { setReconciling(false); }
                }} disabled={reconciling} data-testid="reconcile-this-btn">
                  {reconciling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
                  Reconcile this txn
                </Button>
              )}
              {detail.raw?.webhook_log && detail.raw.webhook_log.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Webhook trail ({detail.raw.webhook_log.length})</div>
                  <pre className="bg-muted rounded-md p-3 text-[11px] overflow-x-auto max-h-60 whitespace-pre-wrap">
                    {JSON.stringify(detail.raw.webhook_log, null, 2)}
                  </pre>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Full row ({detail.table})</div>
                <pre className="bg-muted rounded-md p-3 text-[10px] overflow-x-auto max-h-72 whitespace-pre-wrap">
                  {JSON.stringify(detail.raw, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reconcile-by-txnid dialog */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reconcile by txnid or internal id</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Pulls live status from the gateway for one specific top-up. Use the ManaRecharge txnid (e.g. <span className="font-mono">836034</span>) or our internal UUID.
            </div>
            <Input value={reconcileTxnId} onChange={(e)=>setReconcileTxnId(e.target.value)} placeholder="txnid or internal id" data-testid="reconcile-input" />
          </div>
          <div className="flex justify-end">
            <Button onClick={reconcileSingle} disabled={reconciling} data-testid="reconcile-submit">
              {reconciling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
              Reconcile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function StatCard({ label, count, amount, icon: Icon, accent }) {
  return (
    <div className="surface-card p-4 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-2xl font-display font-bold mt-1 ${accent}`}>{count}</div>
        <div className="text-xs font-mono text-muted-foreground mt-0.5">{formatINR(amount)}</div>
      </div>
      <Icon className={`h-7 w-7 ${accent} opacity-60`} />
    </div>
  );
}

function FilterSelect({ label, value, setValue, options, testId }) {
  return (
    <div className="min-w-[140px]">
      <label className="text-xs text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="text-sm font-medium mt-0.5 break-all">{v}</div>
    </div>
  );
}
